import type { ApplePayConfig } from '@recurly/recurly-js';
import classNames from 'classnames';
import { ComponentProps, Dispatch, ReactNode, SetStateAction } from 'react';
import { FormattedMessage } from 'react-intl';
import type PaymentMethod from '@alltrails/amplitude/enums/PaymentMethod';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import logPurchaseButtonTapped from '@alltrails/amplitude/events/logPurchaseButtonTapped';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import Button from '@alltrails/denali/components/Button';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import ProResponse from '../../types/ProResponse';
import getPurchaseScreenTypePlan from '../../utils/getPurchaseScreenTypePlan';
import ApplePayButtonWithSDK from './ApplePayButtonWithSDK';
import GooglePayButton from './GooglePayButton';
import PayPalButton from './PayPalButton';
import { PaymentOptionValues } from './RadioPaymentForm/PaymentOptions';

type Props = {
  applePayConfig: ApplePayConfig;
  buttonClassName?: string;
  coupon?: Coupon;
  creditCardButtonText?: ReactNode;
  currencyCode: CurrencyCode;
  onApplePayToken: ComponentProps<typeof ApplePayButtonWithSDK>['onApplePayToken'];
  onGooglePayToken: ComponentProps<typeof GooglePayButton>['onGooglePayToken'];
  onPayPalToken: ComponentProps<typeof PayPalButton>['onPayPalToken'];
  plan: Plan;
  purchaseTier: PurchaseTier;
  selectedPaymentType?: PaymentOptionValues;
  setErrors: Dispatch<SetStateAction<ProResponse['errors'] | undefined>>;
  setSubmitting: Dispatch<SetStateAction<boolean>>;
  submitting: boolean;
  submittingCreditCard: boolean;
  trigger?: UpsellTrigger;
  webProPurchaseLocation: WebProPurchaseLocation;
  validateCompliance: (paymentMethod: PaymentMethod) => boolean;
};

export default function RadioPaymentButtons({
  applePayConfig,
  buttonClassName,
  coupon,
  creditCardButtonText = <FormattedMessage defaultMessage="Continue" />,
  currencyCode,
  onApplePayToken,
  onGooglePayToken,
  onPayPalToken,
  plan,
  purchaseTier,
  selectedPaymentType,
  setErrors,
  setSubmitting,
  submitting,
  submittingCreditCard,
  trigger,
  webProPurchaseLocation,
  validateCompliance
}: Props) {
  const productType = purchaseTier === 'peak' ? 'peak' : 'plus';

  if (selectedPaymentType === PaymentOptionValues.Apple) {
    return (
      <ApplePayButtonWithSDK
        applePayConfig={applePayConfig}
        coupon={coupon}
        currencyCode={currencyCode}
        onApplePayToken={onApplePayToken}
        plan={plan}
        productType={productType}
        roundedCorners
        showContinueWith
        setErrors={setErrors}
        setSubmitting={setSubmitting}
        webProPurchaseLocation={webProPurchaseLocation}
        validateCompliance={validateCompliance}
      />
    );
  }

  if (selectedPaymentType === PaymentOptionValues.PayPal) {
    return (
      <PayPalButton
        coupon={coupon}
        onPayPalToken={onPayPalToken}
        plan={plan}
        purchaseTier={purchaseTier}
        darkVariant
        roundedCorners
        showContinueWith
        setErrors={setErrors}
        setSubmitting={setSubmitting}
        submitting={submitting}
        productType={productType}
        webProPurchaseLocation={webProPurchaseLocation}
        validateCompliance={validateCompliance}
      />
    );
  }

  if (selectedPaymentType === PaymentOptionValues.GooglePay) {
    return (
      <GooglePayButton
        coupon={coupon}
        currencyCode={currencyCode}
        onGooglePayToken={onGooglePayToken}
        plan={plan}
        roundedCorners
        setErrors={setErrors}
        setSubmitting={setSubmitting}
        fullWidth
        productType={productType}
        webProPurchaseLocation={webProPurchaseLocation}
        validateCompliance={validateCompliance}
      />
    );
  }

  return (
    <Button
      className={classNames(buttonClassName)}
      loading={submittingCreditCard}
      onClick={() => {
        logPurchaseButtonTapped({
          payment_method: 'credit_card',
          product_type: productType,
          purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
          upsell_trigger: trigger,
          web_purchase_location: webProPurchaseLocation
        });
      }}
      testId="submit"
      text={creditCardButtonText}
      type="submit"
      variant="primary"
      width="full"
    />
  );
}
